/**
 * cww-scroll-reveal.js  —  v2
 * Scroll-triggered animations for the CWW Dream Site.
 * Vanilla JS + CSS only. No libraries, no external downloads.
 *
 * Features:
 *   1. Fade + rise reveals via IntersectionObserver  (data-cww-reveal)
 *   2. Staggered group reveals                       (data-cww-reveal-group)
 *      Children receive data-cww-stagger-index so CSS can delay each one.
 *   3. Directional reveals — slide from left / right (data-cww-reveal="left|right")
 *      Default (no value or "up") → fade + rise.
 *   4. Scale-on-reveal — images/banners gently settle from 1.05 → 1.0
 *      Any element with [data-cww-reveal-scale] gets the scale treatment
 *      (combined with the directional reveal of the parent or itself).
 *   5. Parallax on hero bg image and category banners —
 *      rAF-based, scroll listener, disabled on mobile & prefers-reduced-motion.
 *
 * Classes managed:
 *   .cww-reveal               — base invisible / start state
 *   .cww-reveal--visible      — animated-in (applied by IO callback)
 *   .cww-reveal--from-left    — slides in from left
 *   .cww-reveal--from-right   — slides in from right
 *   .cww-reveal--scale        — adds the gentle scale-settle on reveal
 *   .cww-reveal-group         — parent of staggered children
 *   .cww-reveal-child         — individual staggered child
 *
 * Attributes:
 *   data-cww-reveal           — "up" (default) | "left" | "right"
 *   data-cww-reveal-group     — marks a stagger container
 *   data-cww-reveal-child     — child inside a group (auto-stamped)
 *   data-cww-stagger-index    — 0-based index (auto-stamped by JS)
 *   data-cww-reveal-scale     — add to images/banners for scale-settle
 *   data-cww-parallax-hero    — stamped automatically on .cww-hero__bg-img
 *   data-cww-parallax-banner  — stamped automatically on .cww-cat-banner__img
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /*  ENVIRONMENT CHECKS                                                  */
  /* ------------------------------------------------------------------ */

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Disable parallax on touch / low-power devices or if reduced motion set */
  var isMobile = window.matchMedia('(max-width: 768px)').matches;
  var parallaxEnabled = !prefersReduced && !isMobile;

  /* ------------------------------------------------------------------ */
  /*  CONSTANTS                                                           */
  /* ------------------------------------------------------------------ */

  var IO_THRESHOLD  = 0.12;
  var IO_ROOT_MARGIN = '0px 0px -40px 0px';

  /* How much the hero image drifts relative to scroll (0.25 = 25% of scroll) */
  var HERO_PARALLAX_FACTOR   = 0.22;
  /* Banners are contained elements — keep the offset smaller */
  var BANNER_PARALLAX_FACTOR = 0.15;

  /* ------------------------------------------------------------------ */
  /*  UTILITY                                                             */
  /* ------------------------------------------------------------------ */

  function clamp(val, min, max) {
    return Math.min(Math.max(val, min), max);
  }

  /* ------------------------------------------------------------------ */
  /*  REVEAL — wire up a single element                                   */
  /* ------------------------------------------------------------------ */

  function wireElement(el) {
    if (prefersReduced) {
      el.classList.add('cww-reveal--visible');
      return;
    }

    var dir = el.getAttribute('data-cww-reveal') || 'up';
    el.classList.add('cww-reveal');

    if (dir === 'left')  { el.classList.add('cww-reveal--from-left');  }
    if (dir === 'right') { el.classList.add('cww-reveal--from-right'); }

    /* Scale variant — any element that also carries data-cww-reveal-scale */
    if (el.hasAttribute('data-cww-reveal-scale')) {
      el.classList.add('cww-reveal--scale');
    }
  }

  /* ------------------------------------------------------------------ */
  /*  REVEAL — wire up a stagger group                                    */
  /* ------------------------------------------------------------------ */

  function wireGroup(el) {
    if (prefersReduced) {
      el.classList.add('cww-reveal--visible');
      return;
    }

    el.classList.add('cww-reveal', 'cww-reveal-group');

    var children = el.children;
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      child.classList.add('cww-reveal-child');
      /* Stamp index so CSS can target beyond :nth-child(6) without an endless list */
      child.setAttribute('data-cww-stagger-index', i);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  INTERSECTION OBSERVER callback                                      */
  /* ------------------------------------------------------------------ */

  function onIntersect(entries, observer) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        entry.target.classList.add('cww-reveal--visible');
        observer.unobserve(entry.target);
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  PARALLAX — hero background                                          */
  /* ------------------------------------------------------------------ */

  function initHeroParallax() {
    if (!parallaxEnabled) return;

    /* The hero image — carry data attribute so we can find it quickly */
    var heroImg = document.querySelector('.cww-hero__bg-img');
    if (!heroImg) return;

    heroImg.setAttribute('data-cww-parallax-hero', '');

    /* Give the browser a compositing hint */
    heroImg.style.willChange = 'transform';

    /* Expand the image so it has room to travel without edges showing */
    heroImg.style.height = 'calc(100% + 120px)';
    heroImg.style.top    = '-60px';

    var ticking = false;
    var lastY   = window.scrollY;

    function applyHeroParallax() {
      var hero = heroImg.closest('.cww-hero');
      if (!hero) return;

      var rect     = hero.getBoundingClientRect();
      var viewH    = window.innerHeight;

      /* Only animate while the hero is in or near the viewport */
      if (rect.bottom < -viewH || rect.top > viewH * 2) {
        ticking = false;
        return;
      }

      /* Offset = how far the top of the hero is above the viewport centre */
      var scrolledPast = -rect.top;
      var offset = clamp(scrolledPast * HERO_PARALLAX_FACTOR, -60, 60);

      heroImg.style.transform = 'translateY(' + offset + 'px)';
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      lastY = window.scrollY;
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(applyHeroParallax);
      }
    }, { passive: true });

    /* Kick once on load */
    applyHeroParallax();
  }

  /* ------------------------------------------------------------------ */
  /*  PARALLAX — category banner images                                   */
  /* ------------------------------------------------------------------ */

  function initBannerParallax() {
    if (!parallaxEnabled) return;

    var bannerImgs = document.querySelectorAll('.cww-cat-banner__img');
    if (!bannerImgs.length) return;

    bannerImgs.forEach(function (img) {
      img.setAttribute('data-cww-parallax-banner', '');
      img.style.willChange = 'transform';
      /* Expand vertically so movement doesn't reveal edges */
      img.style.height = 'calc(100% + 80px)';
      img.style.top    = '-40px';
    });

    var ticking = false;

    function applyBannerParallax() {
      var viewH = window.innerHeight;

      bannerImgs.forEach(function (img) {
        var banner = img.closest('.cww-cat-banner');
        if (!banner) return;

        var rect = banner.getBoundingClientRect();

        /* Skip if fully offscreen */
        if (rect.bottom < 0 || rect.top > viewH) return;

        var center      = rect.top + rect.height / 2;
        var fromCenter  = center - viewH / 2;
        var offset      = clamp(fromCenter * BANNER_PARALLAX_FACTOR, -40, 40);

        img.style.transform = 'translateY(' + offset + 'px)';
      });

      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(applyBannerParallax);
      }
    }, { passive: true });

    applyBannerParallax();
  }

  /* ------------------------------------------------------------------ */
  /*  VIEWPORT CHECK — synchronous, reliable on all browsers/devices     */
  /*  Returns true if the element overlaps the current viewport.         */
  /*  Used for the immediate-reveal pass so elements already in view     */
  /*  at load time never sit invisible with a shifted hit area.          */
  /* ------------------------------------------------------------------ */

  function isInViewport(el) {
    var rect = el.getBoundingClientRect();
    return (
      rect.bottom > 0 &&
      rect.right  > 0 &&
      rect.top    < window.innerHeight &&
      rect.left   < window.innerWidth
    );
  }

  /* ------------------------------------------------------------------ */
  /*  INIT                                                                */
  /* ------------------------------------------------------------------ */

  function init() {
    /* ------------------------------------------------------------------ */
    /*  SHOPIFY EDITOR SAFETY                                               */
    /*  In designMode, IntersectionObserver fires unreliably inside editor  */
    /*  iframes. Skip all animation setup and immediately show everything   */
    /*  at rest so the editor always shows aligned, fully-visible elements. */
    /* ------------------------------------------------------------------ */
    var inDesignMode = (typeof Shopify !== 'undefined' && Shopify.designMode);

    if (inDesignMode || prefersReduced) {
      /* Immediately resolve every reveal element to its visible end state.
         No transforms, no opacity — clean at-rest position for all tabs. */
      var allReveal = document.querySelectorAll('[data-cww-reveal]');
      allReveal.forEach(function (el) {
        el.classList.add('cww-reveal--visible');
      });
      var allGroups = document.querySelectorAll('[data-cww-reveal-group]');
      allGroups.forEach(function (el) {
        el.classList.add('cww-reveal--visible');
      });
      /* Parallax also skipped in editor — no scroll events there */
      return;
    }

    /* --- Single-element reveals --- */
    var revealEls = document.querySelectorAll('[data-cww-reveal]');
    revealEls.forEach(wireElement);

    /* --- Group / stagger reveals --- */
    var groupEls = document.querySelectorAll('[data-cww-reveal-group]');
    groupEls.forEach(wireGroup);

    /* --- Also tag images inside reveal elements for scale --- */
    /* Category banner images get scale treatment automatically */
    var bannerImgs = document.querySelectorAll('.cww-cat-banner__img');
    bannerImgs.forEach(function (img) {
      img.classList.add('cww-reveal--scale-img');
    });

    /* --- Immediate-reveal pass — synchronous viewport check -----------
     * IntersectionObserver fires asynchronously. On mobile/touch, elements
     * already in the viewport at load time can miss the first IO callback
     * (timing varies across browsers, especially iOS Safari). If a reveal
     * wrapper — like a .cww-acc-item that contains the accordion trigger
     * button — is stuck in its pre-reveal state (opacity:0, transform:
     * translateY/X) the button is invisible and its hit area is displaced.
     * Taps hit empty space. This pass fires synchronously via
     * getBoundingClientRect() so it is reliable on every device/browser.
     * Any element already in the viewport gets --visible immediately,
     * before the IO callback has a chance to miss it.
     * ----------------------------------------------------------------- */
    var allObserved = [];
    revealEls.forEach(function (el) { allObserved.push(el); });
    groupEls.forEach(function (el)  { allObserved.push(el); });

    allObserved.forEach(function (el) {
      if (isInViewport(el)) {
        el.classList.add('cww-reveal--visible');
      }
    });

    /* --- IntersectionObserver (one shared observer) --- */
    var observer = new IntersectionObserver(onIntersect, {
      threshold:  IO_THRESHOLD,
      rootMargin: IO_ROOT_MARGIN
    });

    /* Only observe elements not already revealed */
    allObserved.forEach(function (el) {
      if (!el.classList.contains('cww-reveal--visible')) {
        observer.observe(el);
      }
    });

    /* --- Safety timeout — belt-and-suspenders for touch devices --------
     * If any reveal element is still hidden 400ms after init (IO misfired,
     * delayed, or the element scrolled into view before IO attached),
     * reveal it now. This guarantees no interactive element is ever left
     * permanently invisible with a displaced touch target.
     * 400ms is after the browser's first paint + iOS Safari's deferred
     * IO callback window, but short enough that a user hasn't yet tapped.
     * ----------------------------------------------------------------- */
    setTimeout(function () {
      allObserved.forEach(function (el) {
        if (!el.classList.contains('cww-reveal--visible')) {
          el.classList.add('cww-reveal--visible');
          observer.unobserve(el);
        }
      });
    }, 400);

    /* --- Parallax --- */
    initHeroParallax();
    initBannerParallax();
  }

  /* Run after DOM is ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
